// ============================================================================
// COMPONENTE: Dialog para editar/reabrir caixa
// ============================================================================
// Funcionalidades:
// - Reabrir caixa (alterar status de fechado para aberto)
// - Visualização de informações do paciente e agendamento
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext";
import {
  FaCashRegister,
  FaUser,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaUserMd,
  FaBuilding,
  FaCalendarAlt,
  FaClock,
  FaDollarSign,
  FaCheckCircle,
} from "react-icons/fa";

const DialogEditarCaixa = ({
  visible,
  onHide,
  reservaEditando,
  onSuccess,
  onError,
  fetchCaixas,
  showSuccessMessage,
  showErrorMessage,
  checked,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const statusOptions = [{ label: "Aberto", value: "aberto" }];

  // Detecção de dispositivo móvel
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Resetar status quando o diálogo for aberto
  useEffect(() => {
    if (visible && reservaEditando) {
      setStatus("");
    }
  }, [visible, reservaEditando]);

  const handleCancelar = () => {
    setStatus("");
    onHide();
  };

  const handleEditar = async () => {
    setLoading(true);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      if (!status) {
        throw new Error("Selecione o status do caixa");
      }

      const caixaData = {
        status: "aberto",
        empresa_id: reservaEditando.empresa_id,
        valor_pago: 0,
        forma_pagamento_id: 0,
        data_fechamento: "",
        usuario_fechamento: "",
        valor_acrescimo: 0,
        valor_desconto: 0,
      };

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixa/${reservaEditando.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(caixaData),
        },
      );

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage =
          responseData?.message ||
          responseData?.data?.message ||
          `Erro HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      if (showSuccessMessage) {
        showSuccessMessage(
          "Caixa reaberto com sucesso! Pronto para novo fechamento.",
        );
      } else if (onSuccess) {
        onSuccess("Caixa reaberto com sucesso! Pronto para novo fechamento.");
      }

      if (fetchCaixas) {
        await fetchCaixas(checked);
      }

      handleCancelar();
    } catch (err) {
      console.error("Erro ao reabrir caixa:", err);
      const errorMsg = err.message.includes("não encontrado")
        ? "Caixa não encontrado ou já removido"
        : err.message;
      if (showErrorMessage) {
        showErrorMessage(errorMsg);
      } else if (onError) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return "-";
    try {
      if (dataStr.includes(" ")) dataStr = dataStr.split(" ")[0];
      else if (dataStr.includes("T")) dataStr = dataStr.split("T")[0];
      const [ano, mes, dia] = dataStr.split("-").map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString("pt-BR");
    } catch (e) {
      return dataStr;
    }
  };

  const formatarHorario = (horario) => horario;

  // ==========================================================================
  // TEMPLATES PARA DROPDOWNS (mantém consistência visual)
  // ==========================================================================
  const dropdownItemTemplate = (option) => (
    <span
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "0.9rem",
        padding: "8px 12px",
      }}
    >
      {option.label}
    </span>
  );

  const dropdownValueTemplate = (option, props) => {
    if (!option)
      return (
        <span
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "0.9rem",
            color: "#7f8c8d",
          }}
        >
          {props.placeholder}
        </span>
      );
    return (
      <span
        style={{
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {option.label}
      </span>
    );
  };

  // Estilos compartilhados
  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "768px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={handleCancelar}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaCashRegister
                style={{ fontSize: "2.4rem", color: "#B83732" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                EDITAR
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                CAIXA
              </div>
            </div>
          </div>
        </div>
      }
    >
      {reservaEditando && (
        <>
          {/* CARD ÚNICO */}
          <div
            style={{
              backgroundColor: "#FAFAFA",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {/* ========== SEÇÃO 1: INFORMAÇÕES DO PACIENTE ========== */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Card Informações do Paciente */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaUser style={iconStyle} />
                  <span>INFORMAÇÕES DO PACIENTE</span>
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#4a5568",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Nome:</span>
                    <span style={{ fontWeight: "500", textAlign: "right" }}>
                      {reservaEditando.paciente_nome}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>CPF:</span>
                    <span>{reservaEditando.paciente_cpf}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Telefone:</span>
                    <span>{reservaEditando.paciente_telefone || "---"}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Email:</span>
                    <span>{reservaEditando.paciente_email || "---"}</span>
                  </div>
                </div>
              </div>

              {/* Card Profissional / Empresa */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaBuilding style={iconStyle} />
                  <span>PROFISSIONAL / EMPRESA</span>
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#4a5568",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Profissional:</span>
                    <span style={{ fontWeight: "500" }}>
                      {reservaEditando.profissional_nome}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Especialidade:</span>
                    <span>{reservaEditando.profissional_especialidade}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Empresa:</span>
                    <span>{reservaEditando.empresa_nome}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== SEÇÃO 2: DATA/HORÁRIO E VALORES ========== */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Card Data e Horário */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaCalendarAlt style={iconStyle} />
                  <span>DATA E HORÁRIO</span>
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#4a5568",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Data:</span>
                    <span style={{ fontWeight: "500" }}>
                      {formatarData(reservaEditando.data_atendimento)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Horário:</span>
                    <span>{formatarHorario(reservaEditando.horario)}</span>
                  </div>
                </div>
              </div>

              {/* Card Valores do Caixa */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaDollarSign style={iconStyle} />
                  <span>VALORES DO CAIXA</span>
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#4a5568",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Status atual:</span>
                    <span
                      style={{
                        fontWeight: "600",
                        color:
                          reservaEditando.status === "fechado"
                            ? "#27ae60"
                            : "#e67e22",
                      }}
                    >
                      {reservaEditando.status?.toUpperCase()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Valor Total:</span>
                    <span style={{ fontWeight: "500" }}>
                      R${" "}
                      {parseFloat(reservaEditando.valor_total || 0).toFixed(2)}
                    </span>
                  </div>
                  {reservaEditando.data_fechamento && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "#64748b" }}>Fechamento:</span>
                      <span>
                        {formatarData(reservaEditando.data_fechamento)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ========== SEÇÃO 3: SITUAÇÃO DO CAIXA ========== */}
            <div>
              <div style={labelStyle}>
                <FaCheckCircle style={iconStyle} />
                <span>SITUAÇÃO DO CAIXA *</span>
              </div>
              <Dropdown
                value={status}
                onChange={(e) => setStatus(e.value)}
                options={statusOptions}
                placeholder="Selecione o status"
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
                itemTemplate={dropdownItemTemplate}
                valueTemplate={dropdownValueTemplate}
                appendTo={document.body} // Fix para mobile
              />
              {status === "aberto" && (
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#27ae60",
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  Ao reabrir, o caixa ficará disponível para novo fechamento
                </span>
              )}
            </div>
          </div>

          {/* ========== BOTÕES DE AÇÃO ========== */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              flexWrap: "wrap",
              marginTop: "24px",
            }}
          >
            {/* Botão Cancelar */}
            <Button
              label="CANCELAR"
              icon="pi pi-times"
              onClick={handleCancelar}
              className="p-button-outlined"
              style={{
                padding: "10px 28px",
                minWidth: "120px",
                borderColor: "#e2e8f0",
                color: "#718096",
                fontWeight: "600",
                borderRadius: "8px",
              }}
              disabled={loading}
            />

            {/* Botão Criar Agendamento */}
            <Button
              label={loading ? "SALVANDO..." : "REABRIR CAIXA"}
              icon="pi pi-check"
              onClick={handleEditar}
              className="p-button-success p-button-sm"
              style={{
                padding: "10px 28px",
                minWidth: "180px",
                fontWeight: "600",
                borderRadius: "8px",
                color: "white",
              }}
              disabled={!status || loading}
              loading={loading}
            />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default DialogEditarCaixa;
